import { useMutation } from "@apollo/client";
import {
  Badge,
  Button,
  FileInput,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { CHECK_USER_BY_PHONE } from "apollo/mutuations";
import axios from "axios";
import { useMemo, useState } from "react";
import { FileSpreadsheet, MapPin } from "tabler-icons-react";
import { API } from "utiles/url";
import * as XLSX from "xlsx";

const RetailerImport = ({ opened, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("25");
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [checkUserByPhone] = useMutation(CHECK_USER_BY_PHONE);

  const handleFileChange = async (selectedFile) => {
    setFile(selectedFile);
    if (!selectedFile) {
      setParsedData([]);
      setCurrentPage(1);
      return;
    }

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const cleanedData = jsonData.map((row) => {
        const cleanedRow = {};
        Object.keys(row).forEach((key) => {
          const trimmedKey = key.trim();
          cleanedRow[trimmedKey] = row[key];
        });
        return cleanedRow;
      });

      console.log("Cleaned JSON Data:", cleanedData);

      const transformed = cleanedData.map((row, index) => ({
        name: (row.Name || row.name || "").toString().trim(),
        customer_id: row.Customer_ID || row.customer_id || "",
        region: (row.Region || row.region || "").toString().trim(),
        city: (row.City || row.city || "").toString().trim(),
        password: (row.Password || row.password || "").toString().trim(),
        phone: (row.Phone || row.phone || "").toString().trim(),
        lat: 0,
        lng: 0,
        status: "Checking...",
        _index: index,
        _valid: true,
        _errors: [],
      }));

      // Parse separate Lat and Long columns
      transformed.forEach((item) => {
        const rawRow = cleanedData[item._index];

        // Try to get latitude
        const latValue =
          rawRow.Lat ||
          rawRow.lat ||
          rawRow.LAT ||
          rawRow.Latitude ||
          rawRow.latitude;
        if (latValue !== undefined && latValue !== null && latValue !== "") {
          const parsedLat = parseFloat(latValue);
          if (!isNaN(parsedLat)) {
            item.lat = parsedLat;
          }
        }

        // Try to get longitude
        const lngValue =
          rawRow.Long ||
          rawRow.long ||
          rawRow.LONG ||
          rawRow.Longitude ||
          rawRow.longitude ||
          rawRow.Lng ||
          rawRow.lng;
        if (lngValue !== undefined && lngValue !== null && lngValue !== "") {
          const parsedLng = parseFloat(lngValue);
          if (!isNaN(parsedLng)) {
            item.lng = parsedLng;
          }
        }
      });

      // Validate each row
      transformed.forEach((item) => {
        const errors = [];
        if (!item.name) errors.push("Name");
        if (!item.phone) errors.push("Phone");
        if (!item.region) errors.push("Region");
        if (!item.password) errors.push("Password");

        if (errors.length > 0) {
          item._valid = false;
          item._errors = errors;
        }
      });

      setParsedData(transformed);
      setCurrentPage(1); // Reset to first page when new data is loaded

      // Check user status for each phone number
      checkAllUserStatuses(transformed);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Parse Error",
        message: error.message,
      });
      setParsedData([]);
      setCurrentPage(1);
    }
  };

  const checkAllUserStatuses = async (data) => {
    setCheckingStatus(true);
    const updatedData = [...data];

    // for (let i = 0; i < updatedData.length; i++) {
    //   if (updatedData[i].phone) {
    //     try {
    //       const { data: result } = await checkUserByPhone({
    //         variables: { phone: updatedData[i].phone },
    //       });

    //       updatedData[i].status =
    //         result?.checkUserByPhone?.status === "Existing"
    //           ? "Existing"
    //           : "New";
    //     } catch (error) {
    //       updatedData[i].status = "Error";
    //     }
    //   } else {
    //     updatedData[i].status = "N/A";
    //   }
    // }

    setParsedData(updatedData);
    setCheckingStatus(false);
  };

  const handleImport = async () => {
    if (!file) {
      showNotification({
        color: "red",
        title: "No File",
        message: "Please select an Excel file first",
      });
      return;
    }

    if (parsedData.length === 0) {
      showNotification({
        color: "red",
        title: "No Data",
        message: "Please select and parse an Excel file first",
      });
      return;
    }

    const invalidRows = parsedData.filter((item) => !item._valid);
    if (invalidRows.length > 0) {
      const errorDetails = invalidRows
        .map(
          (row) => `Row ${row._index + 1}: Missing ${row._errors.join(", ")}`,
        )
        .join("\n");

      showNotification({
        color: "red",
        title: "Validation Error",
        message: `${invalidRows.length} row(s) have missing required fields:\n${errorDetails}`,
        autoClose: 10000,
      });
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token =
        localStorage.getItem("auth_token") || localStorage.getItem("token");

      const response = await axios.post(
        `${API}/bulk-import/retailers`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : "",
          },
        },
      );

      setImporting(false);

      const isSuccess = response.data.message || response.data.summary;

      if (isSuccess) {
        const summary = response.data.summary;
        const message =
          response.data.message ||
          `Import complete. Created: ${summary?.created || 0}, Skipped: ${summary?.skipped || 0}, Failed: ${summary?.failed || 0}`;

        showNotification({
          color: summary?.failed > 0 ? "yellow" : "green",
          title: "Import Complete",
          message: message,
        });

        if (onSuccess) onSuccess();
        handleClose();
      } else {
        showNotification({
          color: "red",
          title: "Import Failed",
          message: response.data.message || "Failed to import retailers",
        });
      }
    } catch (error) {
      setImporting(false);
      console.error("Import error:", error);
      showNotification({
        color: "red",
        title: "Import Failed",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred while importing retailers",
      });
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setCurrentPage(1);
    setRowsPerPage("25");
    onClose();
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * parseInt(rowsPerPage);
    const end = start + parseInt(rowsPerPage);
    return parsedData.slice(start, end);
  }, [parsedData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(parsedData.length / parseInt(rowsPerPage));
  const startIndex = (currentPage - 1) * parseInt(rowsPerPage) + 1;
  const endIndex = Math.min(
    currentPage * parseInt(rowsPerPage),
    parsedData.length,
  );

  const handlePageSizeChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleValidateLocation = (lat, lng) => {
    if (lat && lng && (lat !== 0 || lng !== 0)) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, "_blank");
    } else {
      showNotification({
        color: "red",
        title: "Invalid Location",
        message: "Latitude and Longitude are required",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Retailers"
      size="70%"
      style={{ marginTop: "100px" }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
    >
      <LoadingOverlay visible={importing || checkingStatus} />
      <Stack spacing="md">
        <FileInput
          label="Select Excel File"
          placeholder="Choose .xlsx or .xls file"
          icon={<FileSpreadsheet size={14} />}
          value={file}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          clearable
        />

        {parsedData.length > 0 && (
          <>
            <Group position="apart" align="center">
              <Text size="sm" weight={500}>
                Found {parsedData.length} row(s)
              </Text>
              {parsedData.filter((item) => !item._valid).length > 0 && (
                <Group spacing="xs">
                  <Badge color="red" size="lg">
                    {parsedData.filter((item) => !item._valid).length} invalid
                    row(s)
                  </Badge>
                  <Text size="sm" color="red" weight={600}>
                    Rows:{" "}
                    {parsedData
                      .filter((item) => !item._valid)
                      .map((item) => item._index + 1)
                      .join(", ")}
                  </Text>
                </Group>
              )}
            </Group>

            <ScrollArea style={{ height: 450 }}>
              <Table striped highlightOnHover fontSize="xs">
                <thead style={{ backgroundColor: "#FF6A00" }}>
                  <tr>
                    <th style={{ color: "white" }}>#</th>
                    <th style={{ color: "white" }}>Name</th>
                    <th style={{ color: "white" }}>Customer_ID</th>
                    <th style={{ color: "white" }}>Region</th>
                    <th style={{ color: "white" }}>City</th>
                    <th style={{ color: "white" }}>Password</th>
                    <th style={{ color: "white" }}>Phone</th>
                    <th style={{ color: "white" }}>Status</th>
                    <th style={{ color: "white" }}>Lat</th>
                    <th style={{ color: "white" }}>Long</th>
                    <th style={{ color: "white" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item) => (
                    <tr
                      key={item._index}
                      style={{
                        backgroundColor: !item._valid
                          ? "#ffe6e6"
                          : "transparent",
                      }}
                    >
                      <td>{item._index + 1}</td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Name")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.name || (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td>{item.customer_id}</td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Region")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.region || (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td>{item.city}</td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Password")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.password ? (
                          "*".repeat(item.password.length)
                        ) : (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Phone")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.phone || (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td>
                        <Badge
                          color={
                            item.status === "Existing"
                              ? "blue"
                              : item.status === "New"
                                ? "green"
                                : item.status === "Checking..."
                                  ? "gray"
                                  : "red"
                          }
                          size="sm"
                        >
                          {item.status}
                        </Badge>
                      </td>
                      <td>{item.lat !== 0 ? item.lat : ""}</td>
                      <td>{item.lng !== 0 ? item.lng : ""}</td>
                      <td>
                        {item.lat !== 0 && item.lng !== 0 ? (
                          <Button
                            size="xs"
                            variant="subtle"
                            color="blue"
                            leftIcon={<MapPin size={14} />}
                            onClick={() =>
                              handleValidateLocation(item.lat, item.lng)
                            }
                          >
                            Validate
                          </Button>
                        ) : (
                          <Text size="xs" color="dimmed">
                            N/A
                          </Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
            <Group position="apart" align="center" mt="md">
              <Group spacing="xs" align="center">
                <Text size="sm" color="dimmed">
                  Rows per page:
                </Text>
                <Select
                  value={rowsPerPage}
                  onChange={handlePageSizeChange}
                  data={[
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                    { value: "100", label: "100" },
                  ]}
                  size="xs"
                  style={{ width: 80 }}
                />
                <Text size="sm" color="dimmed">
                  {startIndex}–{endIndex} of {parsedData.length}
                </Text>
              </Group>
              <Pagination
                page={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
                size="sm"
                withEdges
              />
            </Group>
          </>
        )}

        <Group position="right" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedData.length === 0 || importing}
            loading={importing}
            style={{
              backgroundColor: "#FF6A00",
              color: "#FFFFFF",
            }}
          >
            Submit {parsedData.length > 0 && `(${parsedData.length})`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RetailerImport;
